import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Beds, Guest, HotelSample, Location, Share, Sqaure, Success } from '../../assests/ExportImageIcon'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { formatMoney, daysBetween } from '../../utils/formatMoney'
import HotelSearchLoader from '../../componants/hotelSearchLoader/HotelSearchLoader'

const IMPORTANT_INFO = [
    'Carry a valid government-issued photo ID for check-in',
    'Confirm hotel check-in / check-out times before travel',
    'Keep your booking confirmation handy at the front desk',
]

const GoldStar = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill="#F5A623" />
    </svg>
)

const formatBookingDate = (value) => {
    if (!value) return '—'
    const raw = String(value).trim()
    const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/)
    const date = dateOnly ? new Date(`${dateOnly[1]}T00:00:00`) : new Date(raw)
    if (Number.isNaN(date.getTime())) return raw
    const day = date.toLocaleDateString('en-GB', { day: '2-digit' })
    const month = date.toLocaleDateString('en-GB', { month: 'short' })
    const year = date.getFullYear()
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
    return `${day} ${month} ${year}, ${weekday}`
}

const firstValue = (...values) => (
    values.find((value) => value != null && String(value).trim() !== '') || ''
)

/** Verify response uses the same booking fields as the confirmation card. */
const mapVerifyBooking = (body = {}) => {
    const source = body.booking && typeof body.booking === 'object' ? body.booking : body
    const hotel = source.hotel && typeof source.hotel === 'object' ? source.hotel : {}
    const address = source.address || hotel.address
    return {
        ...source,
        booking_id: source.booking_id || source.id || body.booking_id || hotel.booking_id,
        reference_number: source.reference_number || source.referenceNumber || body.reference_number || '',
        hotel_name: source.hotel_name || source.name || hotel.name || hotel.hotel_name || '',
        address: typeof address === 'string' ? address : (address?.address || address?.line1 || ''),
        city: source.city || hotel.city || (typeof address === 'object' ? address.city : '') || '',
        country: source.country || hotel.country || (typeof address === 'object' ? address.country : '') || '',
        rating: source.rating ?? hotel.rating,
        images: source.images || hotel.images || [],
        check_in: firstValue(source.check_in, source.checkin, source.check_in_date, body.checkin, body.check_in),
        check_out: firstValue(source.check_out, source.checkout, source.check_out_date, body.checkout, body.check_out),
        guests: source.guests || body.guests || {},
        guest_room: source.guest_room || source.occupancy || body.guest_room || body.occupancy || [],
        room: source.room || body.room || {},
        payment_summary: source.payment_summary || body.payment_summary || {},
        payment_method: source.payment_method || body.payment_method || body.gateway || '',
        nights: source.nights || body.nights,
    }
}

const formatBookingDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
}

const guestName = (guest) => [guest.title, guest.first_name, guest.last_name].filter(Boolean).join(' ') || 'Guest'

const HotelBookingConfirm = () => {
    const navigate = useNavigate()
    const [searchParamsQuery] = useSearchParams()
    const bookingResult = useSelector((state) => state.hotelSearch.bookingResult)
    const bookingConfirmation = useSelector((state) => state.hotelSearch.bookingConfirmation)
    const searchParams = useSelector((state) => state.hotelSearch.searchParams)
    const paymentIntentId = firstValue(
        bookingConfirmation?.payment_intent_id,
        bookingConfirmation?.paymentIntent?.id,
        bookingResult?.payment_intent_id,
        bookingResult?.paymentIntent?.id,
        bookingResult?.data?.payment_intent_id,
        searchParamsQuery.get('payment_intent'),
    )
    const bookingId = bookingResult?.booking_id
        || bookingResult?.data?.booking_id
        || bookingResult?.id
        || bookingConfirmation?.booking_id
        || null

    const [booking, setBooking] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!paymentIntentId && !bookingId) {
            setLoading(false)
            setError('No booking to show.')
            return undefined
        }

        let cancelled = false
        setLoading(true)
        setError('')

        const loadTrip = () => {
            if (!bookingId) {
                if (!cancelled) setError('Could not load your booking.')
                return Promise.resolve()
            }
            return service
                .get(endpoints.users.tripDetail.replace('{booking_id}', bookingId), {}, { silent: true })
                .then(({ data }) => {
                    if (!cancelled) setBooking(mapVerifyBooking(data.data || data))
                })
        }

        const request = paymentIntentId
            ? service
                .post(endpoints.hotel.bookingVerify, { payment_intent_id: paymentIntentId }, { silent: true })
                .then(({ data }) => {
                    if (cancelled) return
                    setBooking(mapVerifyBooking(data?.data ?? data))
                })
                .catch(() => loadTrip())
            : loadTrip()

        Promise.resolve(request)
            .catch((err) => {
                if (!cancelled) setError(err.response?.data?.message || 'Could not load your booking.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [paymentIntentId, bookingId])

    if ((!paymentIntentId && !bookingId) || error) {
        return (
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='bookingTextConfirm'>
                        <div className='bookingHead commonGredientText'>No booking to show</div>
                        <p>{error || 'Complete a hotel payment to see your booking confirmation here.'}</p>
                    </div>
                    <button type='button' className='commonBtn' onClick={() => navigate('/home')}>Go to Home</button>
                </div>
            </section>
        )
    }

    if (loading || !booking) {
        return (
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <HotelSearchLoader text='Loading your booking...' />
                </div>
            </section>
        )
    }

    const rating = Math.max(0, Math.min(5, Math.round(Number(booking.rating) || 0)))
    const locationLabel = [booking.address, booking.city, booking.country].filter(Boolean).join(', ') || '—'
    const guestRooms = Array.isArray(booking.guest_room) ? booking.guest_room : []
    const roomsCount = guestRooms.length || 1
    const totalAdults = guestRooms.reduce((sum, r) => sum + (Number(r.adult) || 0), 0)
    const totalChildren = guestRooms.reduce((sum, r) => sum + (Number(r.child) || 0), 0)
    const guestsRoomsLabel = [
        `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}`,
        totalChildren > 0 ? `${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}` : null,
        `${roomsCount} Room${roomsCount > 1 ? 's' : ''}`,
    ].filter(Boolean).join(' • ')
    const requestPayload = bookingResult?.requestPayload || bookingResult?.payload || {}
    const checkIn = firstValue(
        booking.check_in,
        booking.checkin,
        booking.check_in_date,
        booking.checkIn,
        booking.stay?.check_in,
        booking.stay?.checkin,
        requestPayload.checkin,
        requestPayload.check_in,
        searchParams.checkIn,
    )
    const checkOut = firstValue(
        booking.check_out,
        booking.checkout,
        booking.check_out_date,
        booking.checkOut,
        booking.stay?.check_out,
        booking.stay?.checkout,
        requestPayload.checkout,
        requestPayload.check_out,
        searchParams.checkOut,
    )
    const nights = Number(booking.nights) || daysBetween(checkIn, checkOut) || 1

    const guestsByRoom = booking.guests || {}
    const allGuests = Object.keys(guestsByRoom)
        .sort((a, b) => Number(a) - Number(b))
        .flatMap((roomKey) => {
            const room = guestsByRoom[roomKey] || {}
            return [...(room.adult || []), ...(room.child || [])]
        })

    const payment = booking.payment_summary || {}
    const currency = payment.currency || 'INR'
    const room = booking.room || {}
    const roomGuestCount = totalAdults + totalChildren
    const heroImage = (Array.isArray(booking.images) && booking.images[0])
        || (Array.isArray(room.images) && room.images[0])
        || HotelSample

    return (
        <>
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingTextConfirm'>
                                <img src={Success} />
                                <div className='bookingHead commonGredientText'>Booking Confirmed</div>
                                <p>Your hotel has been successfully booked</p>
                            </div>
                        </div>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='resultCard'>
                                    <div className='bookingConfirmMetaLabel'>Booking ID <strong>#{booking.booking_id || bookingId}</strong></div>

                                    <div className='hotelCard__topRow mt-3'>
                                        <div>
                                            <div className='hotelCard__name'>{booking.hotel_name || 'Hotel'}</div>
                                            <div className='hotelCard__location mt-2'>
                                                <img src={Location} alt="" />
                                                <div className='hotelCard__locationName'>{locationLabel}</div>
                                            </div>
                                            {booking.reference_number ? (
                                                <div className='hotelTripReference mt-2'>
                                                    Reference No. {booking.reference_number}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className='hotelCard__stars'>
                                            {Array.from({ length: rating }).map((_, index) => <GoldStar key={index} />)}
                                        </div>
                                    </div>

                                    <div className='bookingSegmentBox bookingInfoRow'>
                                        <div>
                                            <div className='bookingInfoLabel'>CHECK-IN</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(checkIn)}</div>
                                        </div>
                                        <div className='bookingInfoNights'>— {nights} Night{nights > 1 ? 's' : ''} —</div>
                                        <div className='text-end'>
                                            <div className='bookingInfoLabel'>CHECK-OUT</div>
                                            <div className='bookingInfoDate'>{formatBookingDate(checkOut)}</div>
                                        </div>
                                    </div>

                                    <div className='bsFareRow mt-2'>
                                        <span>GUEST &amp; ROOMS</span>
                                        <span>{guestsRoomsLabel}</span>
                                    </div>

                                    <div className='bsSectionTitle mt-3'>Guests</div>
                                    <ul className='paymentTravellerList'>
                                        {allGuests.length ? allGuests.map((guest, index) => (
                                            <li key={index}>{index + 1}. {guestName(guest)}</li>
                                        )) : (
                                            <li>Guest details unavailable</li>
                                        )}
                                    </ul>

                                    <div className='bookingConfirmActions'>
                                        <button type='button' className='bookingDownloadBtn'>View Booking</button>
                                        <button type='button' className='bookingShareBtn' aria-label="Share">
                                            <img src={Share} alt="" />
                                        </button>

                                    </div>
                                       <button
                                    type='button'
                                    className='commonBtnTransparent w-100 mt-3'
                                    onClick={() => navigate('/profile', { state: { section: 'create-group' } })}
                                >
                                    Create Splitwise Group
                                </button>
                                <button
                                    type='button'
                                    className='commonBtn w-100 mt-3'
                                    onClick={() => navigate('/home')}
                                >
                                    Go to Home
                                </button>
                                </div>


                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Room Details</div>
                                    <div className='bsBox'>
                                        <div className='roomBookingTop'>
                                            <span className='roomBookingImage'>
                                                <img src={heroImage} alt={room.name || 'Room'} />
                                            </span>
                                            <div>
                                                <div className='hotelCard__name'>{room.name || 'Room'}</div>
                                                <div className='roomBookingSpecs'>
                                                    {room.room_size ? (
                                                        <span><img src={Sqaure} alt="" /> {room.room_size} sq ft</span>
                                                    ) : null}
                                                    <span><img src={Guest} alt="" /> {roomGuestCount || 1} Guest{roomGuestCount > 1 ? 's' : ''}</span>
                                                    {room.bed_type ? (
                                                        <span><img src={Beds} alt="" /> {room.bed_type}</span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>

                                        {Array.isArray(room.facilities) && room.facilities.length > 0 && (
                                            <div className='roomBookingAmenities'>
                                                {room.facilities.map((amenity) => (
                                                    <span className='hotelCard__amenityPill' key={amenity}>{amenity}</span>
                                                ))}
                                            </div>
                                        )}

                                        {(room.room_type || room.board_type) && (
                                            <div className='roomBookingPlanTitle'>
                                                {[room.room_type, room.board_type].filter(Boolean).join(' | ')}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Guest Details</div>
                                    <div className='bsBox'>
                                        {allGuests.length ? allGuests.map((guest, index) => (
                                            <div className='bookingGuestCard' key={index}>
                                                <span className='bookingPassengerType'>GUEST {index + 1}</span>
                                                <div className='bookingGuestGrid'>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Guest Name:</div>
                                                        <div className='bookingPassengerDetail'>{guestName(guest)}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Email:</div>
                                                        <div className='bookingPassengerDetail'>{guest.email || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Phone Number:</div>
                                                        <div className='bookingPassengerDetail'>{[guest.phone_code, guest.phone].filter(Boolean).join(' ') || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <div className='bookingConfirmMetaLabel'>Age &amp; Gender:</div>
                                                        <div className='bookingPassengerDetail'>{[guest.dob, guest.gender].filter(Boolean).join(' • ') || '—'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <p className='bookingConfirmSentText'>Guest details unavailable</p>
                                        )}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Confirmation Sent</div>
                                    <div className='bsBox'>
                                        <p className='bookingConfirmSentText'>
                                            We&apos;ve sent your booking confirmation via email, SMS and WhatsApp.
                                        </p>
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'>
                                            <span>Room Price</span>
                                            <span>{formatMoney(payment.room_price, currency)}</span>
                                        </div>
                                        {(payment.taxes || []).map((tax, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{tax.name || 'Tax'}{tax.type === 'percentage' && tax.rate != null ? ` (${tax.rate}%)` : ''}</span>
                                                <span>{formatMoney(tax.amount, currency)}</span>
                                            </div>
                                        ))}
                                        <div className='bsFareRow'>
                                            <span>Convenience Fee</span>
                                            <span>{formatMoney(payment.convenience_fee, currency)}</span>
                                        </div>
                                        {Number(payment.discount) > 0 && (
                                            <div className='bsFareRow bookingDiscountRow'>
                                                <span>Discount Applied</span>
                                                <span>- {formatMoney(payment.discount, currency)}</span>
                                            </div>
                                        )}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{formatMoney(payment.total, currency)}</span>
                                        </div>
                                        <div className='bsFareDivider' />
                                        {booking.payment_method && (
                                            <div className='bsFareRow'>
                                                <span>Payment Method</span>
                                                <span>{booking.payment_method}</span>
                                            </div>
                                        )}
                                        <div className='bsFareRow'>
                                            <span>Transaction ID</span>
                                            <span>{booking.transaction_id || '—'}</span>
                                        </div>
                                        <div className='bsFareRow'>
                                            <span>Payment Date</span>
                                            <span>{formatBookingDateTime(booking.transaction_date)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Important Information</div>
                                    <div className='bsBox'>
                                        <ul className='bsInfoList'>
                                            {IMPORTANT_INFO.map((info, index) => (
                                                <li key={index}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default HotelBookingConfirm
